/* This example requires Tailwind CSS v2.0+ */
import React, { useContext, useMemo, useState } from "react";
import { WorkspaceContext } from "contexts/WorkspaceContext";
import { Capability, modifyFirewallCapability } from "models/Capability.model";
import { toast } from "utils/toast";
import Button from "components/UI-lib/Button";
import FlatCard from "components/UI-lib/FlatCard";
import { classNames } from "components/utils";
import { PortsWarningModal } from "./PortsWarningModal";
import { Environment, FirewallRule } from "components/Environment/utils/types";
import { Badge } from "components/Graphs/Badge";
import { Button as NewButton } from "components/Graphs/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import { Input } from "components/Graphs/Input";
import { CreateFirewallRule } from "components/Environment/Create/ExposePorts";
import * as AccordionPrimitives from "@radix-ui/react-accordion";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { getEnvironmentStatus } from "components/Environment/utils/environtmentUtils";
import { WorkspaceStatus } from "models/Workspace.model";

interface CloudFirewallPortsSectionProps {
  environment: Environment;
  capabilities: Capability[];
  refetchEnvironment?: () => Promise<unknown>;
}

interface PortGroup {
  id: string;
  fromPort: number;
  toPort: number;
  ip_ranges: string[];
  labels: Record<string, string>;
}

export const getPortGroupsFromIngressRules = (
  ingressRules: FirewallRule[],
): PortGroup[] => {
  const groups: PortGroup[] = [];

  ingressRules.forEach((rule) => {
    if (rule?.from_port === undefined || rule?.to_port === undefined) {
      return;
    }

    const fromPort = Number(rule.from_port);
    const toPort = Number(rule.to_port);

    if (isNaN(fromPort) || isNaN(toPort)) {
      return;
    }

    groups.push({
      id: rule.id,
      fromPort,
      toPort,
      ip_ranges: rule.ip_ranges,
      labels: rule.labels,
    });
  });

  return groups;
};

const isRange = (group: PortGroup) => group.fromPort !== group.toPort;

const portGroupKey = (group: PortGroup) =>
  isRange(group) ? `${group.fromPort}-${group.toPort}` : `${group.fromPort}`;

const getIpBadgeLabel = (labels: Record<string, string>) => {
  if (labels && labels["ip-override"] === "user-ip") {
    return "Only Allowed for your IP";
  }
  return "Allowed for Any IP";
};

const CloudFirewallPortsSection: React.FC<CloudFirewallPortsSectionProps> = (
  props,
) => {
  const wsContext = useContext(WorkspaceContext);
  const [loading, setLoading] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [newPortUserWantsToExpose, setNewPortUserWantsToExpose] = useState("");
  const [portsUserAdded, setPortsUserAdded] = useState<string[]>([]);
  const [showPortsWarning, setShowPortsWarning] = useState(false);
  const [newRuleAllowedIPs, setNewRuleAllowedIPs] = useState("all");
  const isDeletingEnvironment =
    getEnvironmentStatus(props.environment) === WorkspaceStatus.Deleting;
  const publicIP = props.environment.instance?.public_ip || "";
  const validatePortInput = (
    input: string,
  ): { isValid: boolean; error: string } => {
    // Remove any whitespace
    input = input.trim();

    // Check if input matches either a single port or port range pattern
    const singlePortPattern = /^\d+$/;
    const portRangePattern = /^\d+-\d+$/;

    if (!singlePortPattern.test(input) && !portRangePattern.test(input)) {
      return {
        isValid: false,
        error: "Must be a single port or port range (80-95)",
      };
    }

    // Check if it's a port range
    if (input.includes("-")) {
      const [startPort, endPort] = input.split("-").map((p) => parseInt(p));

      if (
        startPort < 1 ||
        startPort > 65535 ||
        endPort < 1 ||
        endPort > 65535
      ) {
        return { isValid: false, error: "Ports must be between 1-65535" };
      }

      if (startPort >= endPort) {
        return {
          isValid: false,
          error: "Start port must be less than end port",
        };
      }

      return { isValid: true, error: "" };
    }

    // It's a single port
    const port = parseInt(input);
    if (port < 1 || port > 65535) {
      return { isValid: false, error: "Port must be between 1-65535" };
    }

    return { isValid: true, error: "" };
  };

  const handleAddNewPort = async () => {
    if (loading || isDeletingEnvironment) {
      return;
    }

    if (!props.environment || !newPortUserWantsToExpose) {
      return;
    }

    const validation = validatePortInput(newPortUserWantsToExpose);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }

    setShowPortsWarning(true);
  };

  const confirmAddPort = async () => {
    if (isDeletingEnvironment) {
      return;
    }

    setLoading(true);
    const createFirewallRule: CreateFirewallRule = {
      port: newPortUserWantsToExpose.trim(),
      allowedIPs: "all",
      clientIPs: [],
    };
    if (newRuleAllowedIPs === "user-ip") {
      try {
        // Fetch public IP from ipify
        const response = await fetch("https://api.ipify.org?format=json");
        if (!response.ok) throw new Error("Failed to fetch IP from api.ipify");
        const data: { ip: string } = await response.json();
        const clientIP = data.ip;
        createFirewallRule.clientIPs = [clientIP + "/32"];
        createFirewallRule.allowedIPs = "user-ip";
      } catch (err) {
        console.error("Failed to fetch or send IP", err);
      }
    }

    const res = await wsContext.addNewPortToWorkspace(
      props.environment.workspaceId || "",
      createFirewallRule,
    );
    setLoading(false);
    if (!res.success) {
      toast.error("Failed to expose port", {
        description: res.message,
      });
    } else {
      await props.refetchEnvironment?.();
      if (newPortUserWantsToExpose.includes("-")) {
        const [startPort, endPort] = newPortUserWantsToExpose
          .split("-")
          .map((p) => parseInt(p));
        // Create array of all ports in range and update state once
        const newPorts = Array.from(
          { length: endPort - startPort + 1 },
          (_, i) => (startPort + i).toString(),
        );
        setPortsUserAdded((prevPorts) => [...prevPorts, ...newPorts]);
      } else {
        setPortsUserAdded([...portsUserAdded, newPortUserWantsToExpose]);
      }
      setNewPortUserWantsToExpose("");
    }
  };

  const revokePortGroup = async (group: PortGroup) => {
    if (isDeletingEnvironment) {
      return;
    }

    setRevokeLoading(true);
    const portString = portGroupKey(group);
    const res = await wsContext.revokePortFromWorkspace(
      props.environment.workspaceId || "",
      portString,
    );
    setRevokeLoading(false);
    if (!res.success) {
      toast.error("Failed to revoke port", {
        description: res.message,
      });
    } else {
      if (props.environment?.instance?.firewall_rules) {
        props.environment.instance.firewall_rules.ingress_rules =
          props.environment.instance.firewall_rules.ingress_rules.filter(
            (rule) => rule.id !== group.id,
          );
      }
      setPortsUserAdded(portsUserAdded.filter((p) => p !== portString));
      await props.refetchEnvironment?.();
    }
  };
  const canModifyFirewallCapability = useMemo(() => {
    return !!props.capabilities.find(
      (capability) => capability === modifyFirewallCapability,
    );
  }, [props.capabilities]);

  return (
    <div className="flex flex-col justify-start items-start">
      <PortsWarningModal
        show={showPortsWarning}
        setShow={setShowPortsWarning}
        onSuccess={() => {
          setShowPortsWarning(false);
          confirmAddPort();
        }}
      />
      {!canModifyFirewallCapability && (
        <span className={classNames("text-rose-500 text-sm mb-3")}>
          This cloud provider doesn't allow the modifications of ports
        </span>
      )}
      <FlatCard
        className={classNames(
          "mb-3 bg-secondaryBg",
          !canModifyFirewallCapability ? "opacity-45" : "",
        )}
      >
        <>
          <div className="flex flex-row justify-between items-center w-full mb-2">
            <div className="mt-3 flex flex-row gap-2">
              <Input
                placeholder="Expose Port(s) (e.g. 2000 or 2000-2020)"
                value={newPortUserWantsToExpose}
                onChange={(e) => setNewPortUserWantsToExpose(e.target.value)}
                className="float-right min-w-[290px] h-10"
                disabled={isDeletingEnvironment}
              />
              <Select
                value={newRuleAllowedIPs}
                onValueChange={(value) => setNewRuleAllowedIPs(value)}
                disabled={isDeletingEnvironment}
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Allow All IPs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Allow All IPs</SelectItem>
                  <SelectItem value="user-ip">Allow Only My IP</SelectItem>
                </SelectContent>
              </Select>
              <NewButton
                variant="primary"
                className="h-10"
                isLoading={loading}
                loadingText="Exposing..."
                onClick={() => handleAddNewPort()}
                disabled={!canModifyFirewallCapability || isDeletingEnvironment}
              >
                Expose Port
              </NewButton>
            </div>
          </div>
          <div className="flex flex-row justify-between items-center w-full mb-1">
            <div className="w-full">
              {props.environment?.instance?.firewall_rules && (
                <div className="w-full">
                  {[
                    ...getPortGroupsFromIngressRules(
                      props.environment?.instance?.firewall_rules
                        ?.ingress_rules || [],
                    ),
                  ]
                    .sort((a, b) => {
                      if (a.fromPort !== b.fromPort) {
                        return a.fromPort - b.fromPort;
                      }
                      return a.toPort - b.toPort;
                    })
                    .map((group) =>
                      isRange(group) ? (
                        <PortRangeItem
                          key={group.id}
                          url={publicIP}
                          group={group}
                          revokePortGroup={revokePortGroup}
                          isLoading={revokeLoading}
                          isDisabled={isDeletingEnvironment}
                        />
                      ) : (
                        <PortUrlItem
                          key={group.id}
                          url={publicIP}
                          group={group}
                          isSSHPort={
                            group.fromPort ===
                            props.environment?.instance?.ssh_port
                          }
                          revokePortGroup={revokePortGroup}
                          showRevoke
                          isLoading={revokeLoading}
                          isDisabled={isDeletingEnvironment}
                        />
                      ),
                    )}
                </div>
              )}
            </div>
          </div>
        </>
      </FlatCard>
    </div>
  );
};

export default CloudFirewallPortsSection;

interface PortUrlItemProps {
  url: string;
  group: PortGroup;
  isSSHPort: boolean;
  revokePortGroup: (group: PortGroup) => void;
  showRevoke: boolean;
  isLoading: boolean;
  isDisabled?: boolean;
}

const PortUrlItem: React.FC<PortUrlItemProps> = ({
  url,
  group,
  isSSHPort,
  revokePortGroup,
  showRevoke,
  isLoading,
  isDisabled = false,
}) => {
  const portStr = group.fromPort.toString();
  return (
    <div className="w-full rounded border-solid border bg-white dark:border-zinc-800 dark:bg-zinc-900 flex items-center justify-start mb-2">
      <div className="flex text-secondary bg-gray-50 dark:text-secondary dark:bg-zinc-900 rounded-tr-none rounded-br-none rounded-r-none border-solid border-r dark:border-zinc-800 p-2 whitespace-nowrap">
        {group.fromPort}
      </div>

      {validUrl(isSSHPort, url) ? (
        <div className="flex w-full">
          <div className="py-1 px-3 text-base truncate underline ml-1 text-highlight dark:border-zinc-800 dark:placeholder-gray-500 hover:text-highlightLighter mt-1 flex-1 dark:bg-zinc-900">
            <a
              href={getUrlToLink(isSSHPort, portStr, url)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {getUrlToDisplay(isSSHPort, portStr, url)}
            </a>
          </div>
          {showRevoke && (
            <div className="flex items-center cursor-default">
              <Badge variant="neutral" className="mr-2">
                {getIpBadgeLabel(group.labels)}
              </Badge>
              <Button
                type="secondary"
                label="Revoke Port"
                className="float-right rounded-none border-l-1 border-l-gray-200 dark:border-zinc-800 border-r-0 border-t-0 border-b-0 dark:text-secondary dark:bg-zinc-900 h-10 ml-auto min-w-[130px]"
                onClick={() => {
                  revokePortGroup(group);
                }}
                loading={isLoading}
                disabled={isDisabled || isLoading}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="w-full text-base py-1 px-3 select-none truncate text-sm text-gray-500 dark:text-secondary">
          {getUrlToDisplay(isSSHPort, portStr, url)}
        </div>
      )}
    </div>
  );
};

interface PortRangeItemProps {
  url: string;
  group: PortGroup;
  revokePortGroup: (group: PortGroup) => void;
  isLoading: boolean;
  isDisabled?: boolean;
}

const PortRangeItem: React.FC<PortRangeItemProps> = ({
  url,
  group,
  revokePortGroup,
  isLoading,
  isDisabled = false,
}) => {
  const rangeLabel = `${group.fromPort}-${group.toPort}`;
  const portCount = group.toPort - group.fromPort + 1;
  const ports = Array.from({ length: portCount }, (_, i) => group.fromPort + i);
  const showUrls = !!url;

  return (
    <AccordionPrimitives.Root type="single" collapsible className="w-full mb-2">
      <AccordionPrimitives.Item value={group.id}>
        <div className="w-full rounded border-solid border bg-white dark:border-zinc-800 dark:bg-zinc-900 overflow-hidden">
          <div className="flex items-center">
            <div className="flex text-secondary bg-gray-50 dark:text-secondary dark:bg-zinc-900 rounded-tr-none rounded-br-none rounded-r-none border-solid border-r dark:border-zinc-800 p-2 whitespace-nowrap">
              {rangeLabel}
            </div>
            <AccordionPrimitives.Header className="flex flex-1">
              <AccordionPrimitives.Trigger className="group flex flex-1 cursor-pointer items-center justify-between py-1 px-3 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500">
                <span className="text-gray-500 dark:text-secondary">
                  {portCount} ports
                </span>
                <ChevronDownIcon
                  className="h-4 w-4 shrink-0 text-secondary transition-transform duration-150 ease-out group-data-[state=open]:rotate-180"
                  aria-hidden="true"
                />
              </AccordionPrimitives.Trigger>
            </AccordionPrimitives.Header>
            <div className="flex items-center cursor-default">
              <Badge variant="neutral" className="mr-2">
                {getIpBadgeLabel(group.labels)}
              </Badge>
              <Button
                type="secondary"
                label="Revoke Range"
                className="rounded-none border-l-1 border-l-gray-200 dark:border-zinc-800 border-r-0 border-t-0 border-b-0 dark:text-secondary dark:bg-zinc-900 h-10 min-w-[130px]"
                onClick={() => revokePortGroup(group)}
                loading={isLoading}
                disabled={isDisabled || isLoading}
              />
            </div>
          </div>
          <AccordionPrimitives.Content className="transform-gpu data-[state=closed]:animate-accordionClose data-[state=open]:animate-accordionOpen">
            <div className="border-t border-solid border-gray-200 dark:border-zinc-800 px-3 py-2">
              <div className="text-xs text-gray-500 dark:text-secondary mb-2">
                Individual ports in this range (revoke applies to the entire
                range):
              </div>
              <div className="flex flex-col gap-1">
                {ports.map((p) => (
                  <div
                    key={p}
                    className="flex items-center bg-gray-50 dark:bg-zinc-950 rounded border-solid border dark:border-zinc-800 px-2 py-1"
                  >
                    <span className="text-secondary dark:text-secondary whitespace-nowrap mr-3 text-sm">
                      {p}
                    </span>
                    {showUrls ? (
                      <a
                        href={`http://${url}:${p}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm truncate underline text-highlight hover:text-highlightLighter"
                      >
                        {`${url}:${p}`}
                      </a>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-secondary truncate">
                        IP will be available when you start the instance
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </AccordionPrimitives.Content>
        </div>
      </AccordionPrimitives.Item>
    </AccordionPrimitives.Root>
  );
};

const getUrlToLink = (isSSHPort: boolean, port: string, url: string) => {
  if (!url) {
    if (isSSHPort) return "SSH";
    return "IP will be available when you start the instance";
  }
  if (isSSHPort) return "SSH";
  return `http://${url}:${port}`;
};

const getUrlToDisplay = (isSSHPort: boolean, port: string, url: string) => {
  if (!url) {
    if (isSSHPort) return "SSH";
    return "IP will be available when you start the instance";
  }
  if (isSSHPort) return "SSH";
  return `${url}:${port}`;
};

const validUrl = (isSSHPort: boolean, url: string) => {
  return url && !isSSHPort;
};
